import { CircularProgress, Box, Collapse, IconButton, Typography } from '@mui/material';
import {
  ContactMail as ContactMailIcon,
  ContactPhone as ContactPhoneIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Fragment, useMemo, useState } from 'react';
import { ServiceRequestWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { useServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { getFormattedDate, LabelValue, LabelValueProps } from '../../../task/common/TaskDetailDrawer';
import moment from 'moment';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import CellRow from 'src/careflow/common/CellRow';
import IcomxInlineTelecomAction from 'src/sections/crs/patient/components/icomx/IcomxInlineTelecomAction';
import { getIcomxCandidateResourceDescriptor } from 'src/sections/crs/patient/components/icomx/icomxContactCandidateModel';
import { useServiceRequestPerformerIcomxCandidates } from 'src/sections/crs/patient/components/icomx/useIcomxContactCandidates';

interface ServiceRequestDetailsContainerProps {
  serviceRequestId: string | undefined;
}

export default function ServiceRequestDetailsContainer({
  serviceRequestId,
}: ServiceRequestDetailsContainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { configurations } = useTenantConfigData();
  const [
    serviceRequest,
    { isFetching: isFetchingServiceRequest },
  ] = useServiceRequest(serviceRequestId, {
    map: ServiceRequestWrapper,
    autofetch: !!serviceRequestId,
  });

  const createdOn = useMemo(() => {
    if (!serviceRequest?.authoredOn) {
      return '-';
    }

    return getFormattedDate(moment(serviceRequest?.authoredOn));
  }, [serviceRequest]);

  const status = useMemo(() => {
    if (!serviceRequest?.status) {
      return '-';
    }

    return serviceRequest?.status;
  }, [serviceRequest]);

  const requesterDisplay = useMemo(() => {
    if (!serviceRequest?.requester?.display) {
      return '-';
    }

    return serviceRequest?.requester?.display;
  }, [serviceRequest]);
  const { candidates: performerCandidates } = useServiceRequestPerformerIcomxCandidates(serviceRequest);
  const performerPhoneCandidate = useMemo(
    () =>
      performerCandidates.find((candidate) => {
        const resource = getIcomxCandidateResourceDescriptor(candidate);
        return !!candidate.phone && !!resource;
      }) ?? null,
    [performerCandidates]
  );
  const performerEmailCandidate = useMemo(
    () =>
      performerCandidates.find((candidate) => {
        const resource = getIcomxCandidateResourceDescriptor(candidate);
        return !!candidate.email && !!resource;
      }) ?? null,
    [performerCandidates]
  );
  const performerPhoneResource = getIcomxCandidateResourceDescriptor(performerPhoneCandidate);
  const performerEmailResource = getIcomxCandidateResourceDescriptor(performerEmailCandidate);
  const srDetails: LabelValueProps[] = [
    {
      label: 'No.',
      value: serviceRequest?.getInternalNumber(
        configurations?.fhir?.serviceRequestInternalNumberIdenitifierSystem
      ),
    },
    { label: 'Created On', value: createdOn },
    { label: 'Status', value: status },
    { label: 'Referred From', value: requesterDisplay },
    {
      label: 'Referred To',
      value: serviceRequest?.getPerformersByReferenceType('PractitionerRole')?.[0]?.display ||
             serviceRequest?.getPerformersByReferenceType('Practitioner')?.[0]?.display ||
             serviceRequest?.getPerformersByReferenceType('Organization')?.[0]?.display ||
             '-',
    },
    { label: 'Service', value: serviceRequest?.getServiceName() ?? '-' },
    { label: 'Location', value: serviceRequest?.getServiceLocationName() ?? '-' },
    { label: 'Assigned To', value: serviceRequest?.getServiceAssignedToName() ?? '-' },
  ];

  if (!serviceRequestId) {
    return null;
  }

  return (
    <Box>
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.95rem',
            letterSpacing: '0.02em',
          }}
        >
          Service Request Details
        </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              size="small"
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease-in-out',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'primary.main',
                },
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
      </Box>
      <Collapse in={isExpanded}>
        <Box>
          {isFetchingServiceRequest ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {srDetails.map((detail) => (
                <Fragment key={detail.label}>
                  <LabelValue {...detail} />
                  {detail.label === 'Referred To' && performerPhoneCandidate?.phone ? (
                    <LabelValue
                      label="Referred To Phone"
                      value={
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <CellRow
                            shouldTruncateText={false}
                            title={performerPhoneCandidate.phone}
                            Icon={<ContactPhoneIcon fontSize="small" />}
                            endAdornment={
                              <IcomxInlineTelecomAction
                                candidates={performerCandidates}
                                resourceType={performerPhoneResource?.resourceType}
                                resourceId={performerPhoneResource?.resourceId}
                                system="phone"
                                tooltip="Open service request phone in iComx"
                                value={performerPhoneCandidate.phone}
                              />
                            }
                          />
                        </Box>
                      }
                    />
                  ) : null}
                  {detail.label === 'Referred To' && performerEmailCandidate?.email ? (
                    <LabelValue
                      label="Referred To Email"
                      value={
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <CellRow
                            shouldTruncateText={false}
                            title={performerEmailCandidate.email}
                            Icon={<ContactMailIcon fontSize="small" />}
                            endAdornment={
                              <IcomxInlineTelecomAction
                                candidates={performerCandidates}
                                resourceType={performerEmailResource?.resourceType}
                                resourceId={performerEmailResource?.resourceId}
                                system="email"
                                tooltip="Open service request email in iComx"
                                value={performerEmailCandidate.email}
                              />
                            }
                          />
                        </Box>
                      }
                    />
                  ) : null}
                </Fragment>
              ))}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
